import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocumentPreviewKind } from './document-preview.types';

//REV-21363 — the SHARED document preview (Pure View). Contract: e2e-lab/document-preview.lab.spec.ts.
//
//SKELETON: the I/O below is final, the template is deliberately empty — the contract is red first.
//
//EXTRACTED from `document-detail.component` (REV-21368). That component now CONSUMES this one; the
//preview markup and the scheme allowlist below moved here unchanged. The extraction is deliberate
//and belongs to REV-21363: AC2 needs the same preview, and copying a security control is the worst
//available outcome — two copies drift, and the copy inherits none of the original's tests.
//
//Dumb by construction (ADR-0009 §10/§13): no store, no HTTP, and it NEVER fetches the file. It
//hands a URL to the browser's own viewer. No pdf.js, no custom renderer HERE.
//
//REV-21369 — the PDF viewer is a SEPARATE component (`abcp-pdf-viewer`, ADR-0021) that the hosts
//project in this one's place — on the single-document page AND in the account's documents pane
//(ADR-0021 Nachtrag N3). This preview stays for what the viewer cannot show: non-PDF files and
//documents not yet released by the scan. ⚠️ `previewUrl` is
//NOT a PDF — it is the first-page PNG the preview worker renders (ADR-0009 §13, marked by ADR-0021).

/**
 * The only schemes the preview will embed. `previewUrl` is handed to
 * `bypassSecurityTrustResourceUrl`, which switches Angular's URL sanitiser OFF — so whatever
 * arrives is trusted verbatim, and a `javascript:` URL would execute.
 *
 * Today the URL comes from our own API, so this cannot happen. That is precisely why the guard is
 * here: a bypass whose safety rests on an assumption about a caller is one refactor away from
 * being wrong, and this component cannot see its caller.
 *
 * ⚠️ This is finding F1 of `docs/security/REV-21368.md` (Medium, fixed). It moved here WHOLE —
 * allowlist, resolution rule and reasoning. Do not narrow it to a `!!url` check again.
 */
const EMBEDDABLE_PREVIEW_SCHEMES = ['http:', 'https:', 'blob:'];

/** Relative URLs are resolved against the document, which is what the container hands over. */
const isEmbeddablePreviewUrl = (url: string): boolean => {
    try {
        return EMBEDDABLE_PREVIEW_SCHEMES.includes(new URL(url, window.location.origin).protocol);
    } catch {
        return false;
    }
};

@Component({
    selector: 'abcp-document-preview',
    templateUrl: './document-preview.component.html',
    styleUrls: ['./document-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpDocumentPreviewComponent {
    /** `null` while the pipeline has not released the file (scanStatus != CLEAN). */
    @Input() previewUrl: string | null = null;

    /** `null` when there is no document at all — renders nothing rather than a wrong placeholder. */
    @Input() previewKind: DocumentPreviewKind | null = null;

    /**
     * Becomes the `<iframe title>`. Without it a screen reader announces only "embedded frame";
     * the filename is what tells the user WHICH document they are looking at.
     */
    @Input() title = '';

    @Input() loading = false;
    @Input() error = false;

    /**
     * Memoised so the binding is a STABLE object: a fresh `SafeResourceUrl` on every change
     * detection cycle would re-set `src` and make the viewer reload in a loop.
     */
    private trustedUrl: string | null = null;
    private trustedSrc: SafeResourceUrl | null = null;

    constructor(private readonly sanitizer: DomSanitizer) {}

    /**
     * The embed only renders for a PDF whose URL is present AND of an embeddable scheme. Anything
     * else falls back to the missing-preview placeholder — a document nobody can display beats a
     * URL nobody vetted.
     */
    get isEmbeddable(): boolean {
        return this.previewKind === 'pdf' && !!this.previewUrl && isEmbeddablePreviewUrl(this.previewUrl);
    }

    /** About the FILE, not the link — and a regular state, not an error (§12). */
    get isUnsupported(): boolean {
        return this.previewKind === 'other';
    }

    /**
     * A PDF the viewer cannot show: the pipeline has not released a URL yet, or the URL is not
     * embeddable. Distinct from `unsupported`, which is about the FILE, not the link.
     */
    get isMissing(): boolean {
        return this.previewKind === 'pdf' && !this.isEmbeddable;
    }

    /**
     * The URL handed to the browser's own viewer. It is trusted ONLY because `isEmbeddable` has
     * already vetted its scheme — the bypass is the second gate here, never the first.
     */
    get previewSrc(): SafeResourceUrl | null {
        const url = this.isEmbeddable ? this.previewUrl : null;
        if (url !== this.trustedUrl) {
            this.trustedUrl = url;
            this.trustedSrc = url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
        }
        return this.trustedSrc;
    }
}
